// Single source of truth for turning a circle's cadence (intervalUnit + intervalCount)
// into display copy. Used anywhere a circle's contribution/payout frequency is shown,
// so "Monthly" is never hardcoded.

export type IntervalUnit = "day" | "week" | "month";

export interface CadenceLabel {
  adjective: string; // e.g. "Daily", "Weekly", "Bi-weekly", "Monthly"
  noun: string; // e.g. "day", "week", "2 weeks", "month"
  suffix: string; // compact amount adornment, e.g. "/day", "/wk", "/2wk", "/mo"
}

const PRESETS: Record<string, CadenceLabel> = {
  "day:1": { adjective: "Daily", noun: "day", suffix: "/day" },
  "week:1": { adjective: "Weekly", noun: "week", suffix: "/wk" },
  "week:2": { adjective: "Bi-weekly", noun: "2 weeks", suffix: "/2wk" },
  "month:1": { adjective: "Monthly", noun: "month", suffix: "/mo" },
};

const ADJECTIVE_BY_UNIT: Record<IntervalUnit, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

// Defaults to monthly when cadence is missing (legacy circles) so existing UIs are
// unchanged. Handles arbitrary counts (e.g. "Every 3 days") beyond the create presets.
export function getCadence(
  unit?: IntervalUnit | string | null,
  count?: number | null
): CadenceLabel {
  const u = (unit as IntervalUnit) || "month";
  const c = count && count > 0 ? count : 1;

  const preset = PRESETS[`${u}:${c}`];
  if (preset) return preset;

  if (c === 1) {
    return {
      adjective: ADJECTIVE_BY_UNIT[u] ?? "Monthly",
      noun: u,
      suffix: `/${u === "month" ? "mo" : u}`,
    };
  }

  return {
    adjective: `Every ${c} ${u}s`,
    noun: `${c} ${u}s`,
    suffix: `/${c}${u.charAt(0)}`,
  };
}
